import { createGtm, useGtm, VueGtmPlugin } from '@gtm-support/vue-gtm';
import router from '@/modules/router';
import { useSystemStore } from '@/stores/system';
import { storeToRefs } from 'pinia';
import { useAccountStore } from '@/features/Account/stores/account';
import { normalisePhoneE164 } from '@/composables/usePhoneNormalisation';
import {
  GtmEvent,
  GtmRequiredAdditionalEventData,
  GtmViewProductData,
  BasicGtmEvent,
  GtmEventViewProduct,
  GtmEventCustomProductView,
  GtmEventChangedVariant,
  GtmViewCollectionData,
  GtmEventViewCollection,
  GtmEventProductCollection,
  GtmEventViewModal,
  GtmEventCustomAtc,
  GtmEventCartInit,
  GtmAddToCartData,
} from './types';

export const setGtmConsent = (boolean: boolean, init = false): void => {
  sessionStorage.setItem('allowCookies', boolean.toString());
  const consentString = boolean ? 'granted' : 'denied';
  const action = init ? 'default' : 'update';
  const consentObject = {
    ad_user_data: consentString,
    ad_personalization: consentString,
    ad_storage: consentString,
    analytics_storage: consentString,
    wait_for_update: init ? 500 : 'granted',
  };

  const gtagPush = function () {
    // Push directly to dataLayer so GTM consent mode applies correctly.
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };
  // @ts-expect-error need it this way to work with datalayer consent
  gtagPush('consent', action, consentObject);
  if (init) {
    // @ts-expect-error ditto
    gtagPush('set', 'url_passthrough', true);
    // @ts-expect-error ditto
    gtagPush('set', 'ads_data_redaction', true);
  }
};

export const setupInitialGtmConsent = (): void => {
  const systemStore = useSystemStore();
  const { shopAttributes } = storeToRefs(systemStore);
  const { region } = shopAttributes.value;
  const impliedConsent = ['AU', 'US', 'CA'].includes(region);
  const sessionConsent = sessionStorage.getItem('allowCookies') === 'true';
  setGtmConsent(impliedConsent || sessionConsent, true);
};

let consentDefaultAlreadyInDataLayer = false;
const overrideMultipleDefaultConsent = (): void => {
  window.dataLayer = [];
  window.dataLayer.push = function () {
    // eslint-disable-next-line prefer-rest-params
    const isDefaultConsentPush = arguments[0]?.[0] === 'consent' && arguments[0]?.[1] === 'default';
    if (isDefaultConsentPush) {
      if (consentDefaultAlreadyInDataLayer) return;
      consentDefaultAlreadyInDataLayer = true;
    }
    // eslint-disable-next-line prefer-rest-params
    return Array.prototype.push.apply(this, arguments);
  };
};

export const mountGtm = (): VueGtmPlugin => {
  // Shopify injects its own default consent event, so prevent duplicates
  overrideMultipleDefaultConsent();
  setupInitialGtmConsent();
  const gtm = createGtm({
    id: 'GTM-NVLHZ77',
    defer: true,
    compatibility: false,
    enabled: true,
    vueRouter: router,
  });

  return gtm;
};

export const gtmEvent = (): GtmEvent => {
  const gtm = useGtm();
  const requiredGtmData = async (): Promise<GtmRequiredAdditionalEventData> => {
    const accountStore = useAccountStore();
    const { attributes, profile } = storeToRefs(accountStore);
    const { isLoggedIn, loginType } = attributes.value;
    const { email, phone, id } = profile.value || {};
    const systemStore = useSystemStore();
    const { shopAttributes } = storeToRefs(systemStore);
    const phoneE164 = await normalisePhoneE164(phone, shopAttributes.value.region);
    return {
      event_id: crypto.randomUUID(),
      isLoggedIn,
      loginType,
      email,
      phone,
      phone_e164: phoneE164,
      ...(id ? { shopify_cid: id } : {}),
    };
  };

  const trackEvent = async (eventData: BasicGtmEvent): Promise<void> => {
    gtm.trackEvent({
      ...(await requiredGtmData()),
      ...eventData,
    });
  };

  const customPageView = (): BasicGtmEvent => {
    const pageView: BasicGtmEvent = {
      event: 'customPageView',
    };
    trackEvent(pageView);
    return pageView;
  };

  const virtualPageView = (): BasicGtmEvent => {
    const eventData: BasicGtmEvent = {
      event: 'VirtualPageview',
      virtualPageURL: `${window.location.pathname}${window.location.hash}`,
    };
    trackEvent(eventData);
    return eventData;
  };

  const customAtc = async (product: GtmAddToCartData): Promise<GtmEventCustomAtc> => {
    const systemStore = useSystemStore();
    const { shopAttributes } = storeToRefs(systemStore);
    const { currency } = shopAttributes.value;
    const ga4_items = [
      {
        currency: currency.fullCode,
        price: product.price / 100,
        item_id: product.sku.toString(),
        item_name: product.title.toString(),
        quantity: product.quantity,
      },
    ];
    const eventData: GtmEventCustomAtc = {
      event: 'customAtc',
      contentType: 'product',
      contentIDs: product.sku.toString(),
      category: 'products',
      value: (product.price / 100).toString(),
      numItems: product.quantity.toString(),
      contentName: product.title.toString(),
      currency: currency.fullCode,
      totalItemIDs: [`${product.id}`],
      totalNumberItems: product.quantity.toString(),
      totalPrice: ((product.price * product.quantity) / 100).toString(),
      ga4_items,
      ga4_items_json: JSON.stringify(ga4_items),
    };
    trackEvent(eventData);
    return eventData;
  };

  const cartInit = (product: GtmAddToCartData): GtmEventCartInit => {
    const systemStore = useSystemStore();
    const { shopAttributes } = storeToRefs(systemStore);
    const { currency } = shopAttributes.value;
    const eventData: GtmEventCartInit = {
      event: 'cartInit',
      contentType: 'product',
      contentIDs: product.sku.toString(),
      value: (product.price / 100).toString(),
      numItems: product.quantity.toString(),
      contentName: product.title.toString(),
      currency: currency.fullCode,
    };
    trackEvent(eventData);
    return eventData;
  };

  const mountApp = (): BasicGtmEvent => {
    const appMount: BasicGtmEvent = {
      event: 'mount-app',
    };
    trackEvent(appMount);
    return appMount;
  };

  const viewProduct = (productViewData: GtmViewProductData): GtmEventViewProduct => {
    const systemStore = useSystemStore();
    const { shopAttributes } = storeToRefs(systemStore);
    const { currency } = shopAttributes.value;
    const { fullCode } = currency;
    const { sku, name, value, quantity = 1 } = productViewData;
    const productView: GtmEventViewProduct = {
      event: 'view-product',
      category: 'products',
      contents: [
        {
          id: sku,
          quantity,
        },
      ],
      content_ids: sku,
      content_name: name,
      content_type: 'product',
      currency: fullCode,
      numItems: quantity,
      value,
    };
    trackEvent(productView);
    return productView;
  };

  const customProductView = (productViewData: GtmViewProductData): GtmEventCustomProductView => {
    const systemStore = useSystemStore();
    const { shopAttributes } = storeToRefs(systemStore);
    const { currency } = shopAttributes.value;
    const { sku, name, value, quantity = 1 } = productViewData;
    const ga4_items = [
      {
        item_id: sku,
        item_name: name,
        quantity,
        price: value,
        currency: currency.fullCode,
      },
    ];
    const eventData: GtmEventCustomProductView = {
      event: 'customProductView',
      content_ids: sku,
      content_name: name,
      content_type: 'product',
      contents: [{ id: sku, quantity }],
      category: 'products',
      value,
      currency: currency.fullCode,
      numItems: quantity,
      ga4_items_json: JSON.stringify(ga4_items),
    };

    trackEvent(eventData);
    return eventData;
  };

  const changedVariant = (page: string, variant: string): GtmEventChangedVariant => {
    const eventData: GtmEventChangedVariant = {
      event: 'changed_variant',
      page,
      variant,
    };
    trackEvent(eventData);
    return eventData;
  };

  const viewCollection = (collectionViewData: GtmViewCollectionData): GtmEventViewCollection => {
    const { handle, name, products } = collectionViewData;
    const collectionProducts = products.map(({ attributes, content }) => ({
      sku: attributes.sku,
      name: content.title,
    }));
    const collectionView: GtmEventViewCollection = {
      event: 'view-collection',
      handle,
      name,
      collectionProducts,
    };
    trackEvent(collectionView);
    return collectionView;
  };

  const productCollection = (collectionData: GtmViewCollectionData): GtmEventProductCollection => {
    const { handle, products } = collectionData;
    const eventData: GtmEventProductCollection = {
      event: 'productCollection',
      products,
      category: handle,
      collection: handle,
    };

    trackEvent(eventData);
    return eventData;
  };

  const viewModal = (id: string): GtmEventViewModal => {
    const modalView: GtmEventViewModal = {
      event: 'modal_open',
      modal_id: id,
    };
    trackEvent(modalView);
    return modalView;
  };

  return {
    requiredGtmData,
    trackEvent,
    customPageView,
    virtualPageView,
    customAtc,
    cartInit,
    mountApp,
    viewProduct,
    customProductView,
    changedVariant,
    viewCollection,
    productCollection,
    viewModal,
  };
};
